import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Clock } from '@one-thd/sui-icons';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import {
  customType,
  params,
  string as StringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  CardContent,
  CardMedia,
  CardTitle,
  Typography
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import {
  getEndDate,
  getEndDateWithTimezoneOffset,
  getLogoSrc
} from './Helper';
import { FEATURE_SWITCH_KEYS, TITLE } from '../utils/constants';

const SpecialBuyMessaging = ({
  previewDate,
  specialBuyType,
}) => {
  const isDailyDealsEnabled = useConfigService(FEATURE_SWITCH_KEYS.isDailyDealsEnabled);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const isSBOTD = specialBuyType === 'sbotd';

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('special-buy-messaging.ready'); }, []);
  const { data } = useDataModel('specialBuyMetadata', {
    ssr: !previewDate,
    variables: {
      dealType: (isSBOTD ? 'DAY' : 'PRO'),
      previewDate
    },
    context: { withSSO: previewDate }
  });

  const title = isSBOTD ? (isDailyDealsEnabled ? TITLE.DAILY_DEALS : TITLE.SBOTD) : TITLE.SBOTW;
  const content = isSBOTD ? 'Today Only!' : 'Online Only!';
  const countdownTimerHeader = (isSBOTD && isDailyDealsEnabled) ? 'Deals End In' : 'Savings End';
  const specialBuyEndDate = isSBOTD ? getEndDateWithTimezoneOffset(data?.specialBuyMetadata?.endDate, isSBOTD)
    : getEndDate(data?.specialBuyMetadata?.endDate);

  const logoWrapperClass = classNames('sui-pl-2 sui-pr-3', {
    'sui-w-[145px]': (isSBOTD && isDailyDealsEnabled),
    'sui-pt-4': isMobile,
    'sui-pt-1': !isMobile,
  });
  const logoGridContainerClass = classNames({ 'grid': !isMobile });
  const logoGridWrapperClass = classNames('grid-cols-3 sui-pb-6 sui-pl-1', {
    'sui-pt-3': isMobile,
    'sui-pt-10': !isMobile,
  });
  const subheader = (
    <div className="sui-pt-2 sui-pb-2 sui-flex sui-gap-2">
      <Typography color="primary" weight="bold" variant="body-lg">{content}</Typography>
      Free Delivery*
    </div>
  );

  const CustomImage = () => {
    return (
      <img
        src={`https://assets.thdstatic.com/images/v1/${getLogoSrc(isSBOTD, isDailyDealsEnabled)}.svg`}
        alt="Placeholder img"
        height="90"
        width="90"
      />
    );
  };

  return (
    <div className={logoGridContainerClass} data-component="SpecialBuyMessaging">
      <div className={logoGridWrapperClass}>
        <div className=" sui-flex sui-gap-1">
          <CardContent orientation="horizontal" disablePadding>
            <div className={logoWrapperClass}>
              <CardMedia component={CustomImage} alt="Placeholder image" />
            </div>
          </CardContent>
          <CardContent disablePadding disableGutters initialSize="full">
            <CardTitle
              header={<Typography variant={!isMobile ? 'h1' : 'h2'} weight="bold">{title}</Typography>}
              disableTypography="true"
              headerWeight="bold"
              headerVariant="h3"
              subheader={<div className="sui-text-lg sui-pt-1">{subheader}</div>}
            />
          </CardContent>
        </div>
      </div>
      <div className={`sui-ml-auto sui-pr-2 sui-pb-3 ${!isMobile ? 'sui-pt-10' : ''}`}>
        <div className="sui-pr-1" hidden={isMobile}>
          <div className="sui-flex">
            <div className="sui-align-baseline sui-h-6 sui-pt-1 sui-w-6">
              <Clock size="small" color="subtle" />
            </div>
            <div>
              <Typography variant="body-lg"> {countdownTimerHeader} </Typography>
            </div>
          </div>
        </div>
        <div className="sui-pl-6">
          <CountdownTimer
            classes="countdown-timer__wrapper--modifier"
            timebombThreshold={Infinity}
            isPromotionTimer
            shouldShowTextBelow={!isMobile}
            endDate={specialBuyEndDate}
            shouldUseClockDisplay={isMobile}
            shouldIncludeSvg={isMobile}
          />
        </div>
      </div>
    </div>
  );
};

SpecialBuyMessaging.displayName = 'SpecialBuyMessaging';

SpecialBuyMessaging.propTypes = {
  previewDate: PropTypes.string,
  specialBuyType: PropTypes.oneOf(['sbotd', 'pro'])
};

SpecialBuyMessaging.defaultProps = {
  previewDate: null,
  specialBuyType: 'sbotd'
};

SpecialBuyMessaging.dataModel = {
  specialBuyMetadata: params({
    dealType: customType('DealType').enum(['DAY', 'PRO'], 'DAY'),
    previewDate: StringType()
  }).shape({
    endDate: StringType()
  })
};

export { SpecialBuyMessaging };
